import type {RevaLanguage} from "../i18n";
import {selectByLanguage} from "../i18n";
import type {RevaSpecOutputRef} from "../spec-conversion";

// ---------------------------------------------------------------------------
// Basic value/price/area formatting
// ---------------------------------------------------------------------------

const INTL_DE_INDEX = new Intl.NumberFormat("de-DE", {minimumFractionDigits: 1, maximumFractionDigits: 1});
const INTL_EN_INDEX = new Intl.NumberFormat("en-US", {minimumFractionDigits: 1, maximumFractionDigits: 1});

const INTL_DE_VALUE = new Intl.NumberFormat("de-DE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
const INTL_EN_VALUE = new Intl.NumberFormat("en-US", {minimumFractionDigits: 2, maximumFractionDigits: 2});

const INTL_DE_VALUE_NO_FRAC = new Intl.NumberFormat("de-DE", {minimumFractionDigits: 0, maximumFractionDigits: 0});
const INTL_EN_VALUE_NO_FRAC = new Intl.NumberFormat("en-US", {minimumFractionDigits: 0, maximumFractionDigits: 0});

const INTL_DE_VALUE_NO_FRAC_NO_GRP = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: false,
});
const INTL_EN_VALUE_NO_FRAC_NO_GRP = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: false,
});

/** Formats an index value with exactly one decimal place. */
export function formatIndex(value: number | null | undefined, language?: RevaLanguage): string {
    if (typeof value !== "number") return "";
    return selectByLanguage(language, INTL_DE_INDEX, INTL_EN_INDEX).format(value);
}

const INTL_DE_DISTANCE_KM = new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    useGrouping: false,
});
const INTL_EN_DISTANCE_KM = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    useGrouping: false,
});

/** Distance in meters, switching to kilometers (1 decimal, localized separator) from 1000 m. */
export function formatDistanceM(meters: number | null | undefined, language?: RevaLanguage): string {
    if (meters == null) return "-";
    if (meters < 1000) return `${meters} m`;
    return `${selectByLanguage(language, INTL_DE_DISTANCE_KM, INTL_EN_DISTANCE_KM).format(meters / 1000)} km`;
}

/**
 * Formats a plain numeric value.
 * @param value
 * @param language
 * @param noFraction render as an integer (no fraction digits)
 * @param noGrouping render without a thousands separator; only used when noFraction=true
 */
export function formatValue(
    value: number | null | undefined,
    language?: RevaLanguage,
    noFraction?: boolean,
    noGrouping?: boolean,
): string {
    if (typeof value !== "number") return "";
    const nf = noFraction
        ? noGrouping
            ? selectByLanguage(language, INTL_DE_VALUE_NO_FRAC_NO_GRP, INTL_EN_VALUE_NO_FRAC_NO_GRP)
            : selectByLanguage(language, INTL_DE_VALUE_NO_FRAC, INTL_EN_VALUE_NO_FRAC)
        : selectByLanguage(language, INTL_DE_VALUE, INTL_EN_VALUE);
    return nf.format(value);
}

/** Formats a price (in Euro by default). */
export function formatPrice(
    value: number | null | undefined,
    language?: RevaLanguage,
    noFraction?: boolean,
    currency?: string,
): string {
    if (typeof value !== "number") return "";
    return `${formatValue(value, language, noFraction)} ${currency || "€"}`;
}

/** Formats a price per area (in Euro per square meter by default). */
export function formatPricePerArea(
    value: number | null | undefined,
    language?: RevaLanguage,
    noFraction?: boolean,
    unit?: string,
): string {
    if (typeof value !== "number") return "";
    return `${formatValue(value, language, noFraction)} ${unit || "€/m²"}`;
}

/** Formats an area (in square meters by default). */
export function formatArea(
    value: number | null | undefined,
    language?: RevaLanguage,
    noFraction?: boolean,
    unit?: string,
): string {
    if (typeof value !== "number") return "";
    return `${formatValue(value, language, noFraction)} ${unit || "m²"}`;
}

// ---------------------------------------------------------------------------
// GUI-type-driven formatting (spec-derived display formats, e.g. "DOUBLE(0-2)")
// ---------------------------------------------------------------------------

export type RoundingMode = "ceil" | "floor" | "halfExpand";

/** A single selectable category, as used by CATEGORY-typed GUI types. */
export interface GuiTypeCategory {
    categoryNumber: number | string;
    titleDe?: string;
    titleEn?: string;
}

export interface GuiType {
    baseType: "CATEGORY" | "DATE" | "NUMBER" | "BOOLEAN" | "EMPTY" | "TEXT";
    /** minimal number of decimal places (only relevant if baseType=NUMBER) */
    decimalPlacesMin?: number;
    /** maximal number of decimal places (only relevant if baseType=NUMBER) */
    decimalPlacesMax?: number;
    /** flag to always render the sign (only relevant if baseType=NUMBER) */
    alwaysShowSign?: boolean;
    /** flag to group by thousands separator (only relevant if baseType=NUMBER) */
    grouped?: boolean;
    /** optional rounding mode (only relevant if baseType=NUMBER) */
    roundingMode?: RoundingMode;
    /** label for the true value (in language DE, only relevant if baseType=BOOLEAN) */
    labelTrueDE?: string;
    /** label for the true value (in language EN, only relevant if baseType=BOOLEAN) */
    labelTrueEN?: string;
    /** label for the false value (in language DE, only relevant if baseType=BOOLEAN) */
    labelFalseDE?: string;
    /** label for the false value (in language EN, only relevant if baseType=BOOLEAN) */
    labelFalseEN?: string;
    /** unit (in language DE) */
    unitDE?: string | null;
    /** unit (in language EN) */
    unitEN?: string | null;
    /** selectable categories (only relevant if baseType=CATEGORY) */
    categories?: GuiTypeCategory[];
}

/**
 * Parses a GUI type from the given display format string (e.g. "DOUBLE(0-2,+)", "CATEGORY", "CHECKBOX(An,Aus)").
 * @param format display format
 * @param unitDE unit (in language DE)
 * @param unitEN unit (in language EN)
 * @param categories selectable categories (only relevant if format=CATEGORY)
 */
export function parseGuiType(
    format: string | null | undefined,
    unitDE?: string | null,
    unitEN?: string | null,
    categories?: GuiTypeCategory[],
): GuiType {
    if (!format) {
        return {baseType: "EMPTY"};
    }

    let baseType = format;
    let opts: string[] = [];
    if (format.includes("(") && format.endsWith(")")) {
        const split = format
            .substring(0, format.length - 1)
            .split("(", 2)
            .map((item) => item.trim());
        baseType = split[0];
        opts = split[1]
            .split(/[,;]/)
            .map((item) => item.trim())
            .filter((item) => !!item);
    }

    const createEmpty = (error?: string): GuiType => {
        console.warn(`Could not parse GUI Type "${format}${error ? `, because ${error}` : ""}"`);
        return {baseType: "EMPTY"};
    };

    const createTextFormat = (): GuiType => ({baseType: "TEXT"});

    const createNumberFormat = (
        decimalPlacesMin: number,
        decimalPlacesMax: number,
        alwaysShowSign: boolean,
        grouped: boolean,
        roundingMode: RoundingMode | undefined,
    ): GuiType => ({
        baseType: "NUMBER",
        decimalPlacesMin,
        decimalPlacesMax,
        alwaysShowSign,
        grouped,
        unitDE: unitDE || undefined,
        unitEN: unitEN || undefined,
        roundingMode,
    });

    const createNumberFormatWithArgs = (defaultDecimalPlaces: number, grouped: boolean): GuiType => {
        let decimalPlacesMin = defaultDecimalPlaces;
        let decimalPlacesMax = defaultDecimalPlaces;
        let alwaysShowSign = false;
        // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#roundingmode
        let roundingMode: RoundingMode = "floor"; // Per Default: Abrunden, laut #18161
        for (const opt of opts) {
            switch (opt) {
                case "+":
                case "+-":
                    alwaysShowSign = true;
                    break;
                case ">":
                    roundingMode = "ceil"; // Aufrunden
                    break;
                case "~":
                    roundingMode = "halfExpand"; // "Kaufmännisches Runden gemäß DIN (immer von der 0 weg)" / JS-Standard für Runden
                    break;
                case "<":
                    roundingMode = "floor"; // Abrunden
                    break;
                default: {
                    const matchedNr = opt.match(/^(\d+)$/);
                    if (matchedNr) {
                        decimalPlacesMin = Number.parseInt(matchedNr[1], 10);
                        decimalPlacesMax = decimalPlacesMin;
                        break;
                    }
                    const matchedNrRange = opt.match(/^(\d+)-(\d+)$/);
                    if (matchedNrRange) {
                        decimalPlacesMin = Number.parseInt(matchedNrRange[1], 10);
                        decimalPlacesMax = Number.parseInt(matchedNrRange[2], 10);
                    }
                }
            }
        }
        return createNumberFormat(decimalPlacesMin, decimalPlacesMax, alwaysShowSign, grouped, roundingMode);
    };

    const createBooleanFormatWithArgs = (
        defaultLabelTrueDE: string,
        defaultLabelFalseDE: string,
        defaultLabelTrueEN: string,
        defaultLabelFalseEN: string,
    ): GuiType => {
        let labelTrueDE = defaultLabelTrueDE;
        let labelFalseDE = defaultLabelFalseDE;
        let labelTrueEN = defaultLabelTrueEN;
        let labelFalseEN = defaultLabelFalseEN;
        if (opts.length === 2) {
            labelTrueDE = opts[0];
            labelFalseDE = opts[1];
            labelTrueEN = opts[0];
            labelFalseEN = opts[1];
        } else if (opts.length === 4) {
            labelTrueDE = opts[0];
            labelFalseDE = opts[1];
            labelTrueEN = opts[2];
            labelFalseEN = opts[3];
        }
        return {baseType: "BOOLEAN", labelTrueDE, labelFalseDE, labelTrueEN, labelFalseEN};
    };

    switch (baseType.toUpperCase()) {
        case "CATEGORY":
            if (!categories || categories.length <= 1) {
                return createEmpty("no categories were provided!");
            }
            return {baseType: "CATEGORY", categories};
        case "YEAR":
            return createNumberFormat(0, 0, false, false, undefined);
        case "DATE":
            return {baseType: "DATE", unitDE: unitDE || undefined, unitEN: unitEN || undefined};
        case "DOUBLE":
            return createNumberFormatWithArgs(2, true);
        case "INTEGER":
            return createNumberFormatWithArgs(0, true);
        case "CHECKBOX":
            return createBooleanFormatWithArgs("Ja", "Nein", "Yes", "No");
        case "TEXT":
            return createTextFormat();
        default:
            return createEmpty();
    }
}

/**
 * Formats a value according to its GUI type.
 */
export function formatTypedValue(lang: RevaLanguage, value: unknown, guiType: GuiType): string {
    if (typeof value === "undefined" || value === null || !guiType || guiType.baseType === "EMPTY") {
        return "";
    }

    switch (guiType.baseType) {
        case "CATEGORY": {
            const foundCategory = guiType.categories?.find((cat) => cat.categoryNumber === value);
            if (!foundCategory) {
                console.warn(`Could not resolve category for value ${String(value)}`, guiType.categories);
                return selectByLanguage(
                    lang,
                    `Kategorie ${String(value)} (ungültig)`,
                    `Category ${String(value)} (invalid)`,
                );
            }
            return selectByLanguage(lang, foundCategory.titleDe, foundCategory.titleEn) ?? "";
        }
        case "BOOLEAN":
            if (value) {
                return selectByLanguage(lang, guiType.labelTrueDE, guiType.labelTrueEN) ?? "";
            }
            return selectByLanguage(lang, guiType.labelFalseDE, guiType.labelFalseEN) ?? "";
        case "DATE": {
            if (lang === "en") return value as string; // is already in ISO date style
            const date = Date.parse(value as string);
            if (Number.isNaN(date)) return value as string;
            return new Intl.DateTimeFormat("de", {dateStyle: "medium"}).format(date);
        }
        case "NUMBER": {
            const fmt = new Intl.NumberFormat(lang, {
                minimumFractionDigits: guiType.decimalPlacesMin,
                maximumFractionDigits: guiType.decimalPlacesMax,
                useGrouping: guiType.grouped,
                signDisplay: guiType.alwaysShowSign ? "always" : "auto",
                // roundingMode is valid at runtime but missing from the ES2020 lib's NumberFormatOptions
                roundingMode: guiType.roundingMode ?? undefined,
            } as Intl.NumberFormatOptions & {roundingMode?: RoundingMode});
            let formatted = fmt.format(value as number);
            const unit = selectByLanguage(lang, guiType.unitDE, guiType.unitEN);
            if (unit) {
                formatted += ` ${unit}`;
            }
            return formatted;
        }
        case "TEXT":
            return String(value);
        default:
            console.warn(`Invalid GUI base type ${guiType.baseType} for value ${String(value)}`);
            return "";
    }
}

// ---------------------------------------------------------------------------
// Parameter-definition-driven formatting (spec input/output parameters)
// ---------------------------------------------------------------------------

export interface ParameterFormatOptions {
    /** GUI format override */
    formatOverride?: string | null;
    /** flag to omit the unit */
    omitUnit?: boolean;
    /** flag to render category titles instead of their short names */
    useCategoryTitles?: boolean;
}

/** A single selectable category of a parameter definition, as needed for value formatting. */
export interface FormatParameterCategory {
    categoryNumber: number | string;
    titleDe?: string;
    titleEn?: string;
    shortNameDe?: string;
    shortNameEn?: string;
}

/** Parameter definition (from spec), as needed for value formatting. */
export interface FormatParameterDef {
    /** parameter name */
    parameter: string;
    /** the datatype of the parameter */
    type: string;
    /** unit (in language DE) */
    unitDe?: string | null;
    /** unit (in language EN) */
    unitEn?: string | null;
    /** optional formatting of the value */
    displayFormat?: string | null;
    categories?: FormatParameterCategory[] | null;
}

/**
 * Formats a parameter value using its definition.
 */
export function formatParameterValue(
    lang: RevaLanguage,
    parameterDef: FormatParameterDef,
    value: unknown,
    options: ParameterFormatOptions = {},
): string | null {
    if (value === "empty") return lang === "en" ? "Not specified" : "Keine Angabe";
    if (value === null || value === undefined) return null;

    const inferGuiType = (type: string) => {
        const categories = parameterDef.categories?.map((c) => ({
            categoryNumber: c.categoryNumber,
            titleDe: options.useCategoryTitles ? c.titleDe : c.shortNameDe,
            titleEn: options.useCategoryTitles ? c.titleEn : c.shortNameEn,
        }));
        const unitDE = !options.omitUnit ? parameterDef.unitDe : null;
        const unitEN = !options.omitUnit ? parameterDef.unitEn : null;
        return parseGuiType(options.formatOverride ?? type, unitDE, unitEN, categories);
    };

    if (parameterDef.parameter?.indexOf("year") > -1) {
        return formatTypedValue(lang, value, inferGuiType("YEAR"));
    }
    switch (parameterDef.type) {
        case "number":
        case "double":
            return formatTypedValue(lang, value, inferGuiType("DOUBLE(0-2)"));
        case "boolean":
            return formatTypedValue(lang, value, inferGuiType("CHECKBOX"));
        case "integer":
        case "category":
            return formatTypedValue(lang, value, inferGuiType(parameterDef.type.toUpperCase()));
        case "string":
            return formatTypedValue(lang, value, inferGuiType("TEXT"));
        case "date":
            return /\d\d\d\d-\d?\d-\d?\d/.test(value as string)
                ? formatTypedValue(lang, value, inferGuiType("DATE"))
                : (value as string);
        case "category[]":
            return Array.isArray(value)
                ? value.map((v) => formatTypedValue(lang, v, inferGuiType("CATEGORY"))).join(", ")
                : (value as string);
        case "double[]": {
            const guiType = inferGuiType("DOUBLE(0-3)");
            return Array.isArray(value)
                ? value.map((v) => formatTypedValue(lang, v, guiType)).join(", ")
                : formatTypedValue(lang, value, guiType);
        }
        case "string[]":
            return Array.isArray(value) ? value.join(", ") : (value as string);
        default:
            console.warn(`Invalid parameter type ${parameterDef.type} for value ${String(value)}`);
            return value as string;
    }
}

/** Type-driven default GUI format, used when a parameter def has no `displayFormat` of its own. */
const PARAMETER_TYPE_FORMAT_DEFAULTS: Record<string, string> = {
    number: "DOUBLE(0,4)",
    double: "DOUBLE(0,4)",
    integer: "INTEGER",
    category: "CATEGORY",
    string: "TEXT",
    date: "DATE",
    "double[]": "DOUBLE(0,3)",
    "category[]": "CATEGORY",
};

/**
 * Formats a parameter value using its own `displayFormat` (falling back to a type-driven
 * default) — for AVM-spec-derived output parameters, where `displayFormat` is normally always
 * present. Unlike {@link formatParameterValue} (which applies a fixed per-type format and is used
 * for the wider variety of parameter-def shapes elsewhere, e.g. input parameters), this respects
 * per-parameter format overrides from the spec.
 */
export function formatAvmSpecParameter(
    lang: RevaLanguage,
    parameterDef: FormatParameterDef,
    value: unknown,
    options: ParameterFormatOptions,
): string {
    const categories = parameterDef.categories?.map((c) => ({
        categoryNumber: c.categoryNumber,
        titleDe: options.useCategoryTitles ? c.titleDe : c.shortNameDe,
        titleEn: options.useCategoryTitles ? c.titleEn : c.shortNameEn,
    }));
    const unitDe = !options.omitUnit ? parameterDef.unitDe : null;
    const unitEn = !options.omitUnit ? parameterDef.unitEn : null;
    const guiType = parseGuiType(
        options.formatOverride ?? parameterDef.displayFormat ?? PARAMETER_TYPE_FORMAT_DEFAULTS[parameterDef.type],
        unitDe,
        unitEn,
        categories,
    );
    return formatTypedValue(lang, value, guiType);
}

/** A single value range, as needed for range-based value formatting - see `RevaSpecObjectInputRange`. */
export interface FormatValueRange {
    representativeValue: number;
    lowerValue: number;
    upperValue: number;
    nameDe: string;
    nameEn: string;
}

/** Finds the range a numeric value falls into (inclusive bounds); null if none matches or value isn't numeric. */
export function findValueRange(ranges: FormatValueRange[] | null | undefined, value: unknown): FormatValueRange | null {
    if (typeof value !== "number" || !ranges?.length) return null;
    return ranges.find((range) => value >= range.lowerValue && value <= range.upperValue) ?? null;
}

/** Formats a value via its containing range's label, e.g. `63` -> "gut"; null if no range matches. */
export function formatValueRange(
    lang: RevaLanguage,
    value: unknown,
    ranges: FormatValueRange[] | null | undefined,
): string | null {
    const range = findValueRange(ranges, value);
    return range ? (selectByLanguage(lang, range.nameDe, range.nameEn) ?? null) : null;
}

// ---------------------------------------------------------------------------
// Market/lending value formatting
// ---------------------------------------------------------------------------

/** Spec refs describing the format of a market/lending value and its per-sqm variants. */
export interface MarketAndLendingValueSpec {
    market?: RevaSpecOutputRef | null;
    marketSqm?: RevaSpecOutputRef | null;
    lending?: RevaSpecOutputRef | null;
    lendingSqm?: RevaSpecOutputRef | null;
}

/** Raw market/lending value + per-sqm variants, as returned by a value indication. */
export interface MarketAndLendingValue {
    value?: number | string | null;
    value_per_sqm?: number | string | null;
    lending_value?: number | string | null;
    lending_value_per_sqm?: number | string | null;
}

export interface FormattedMarketAndLendingValue {
    value: string;
    valueSqm: string;
    valueLending: string;
    valueLendingSqm: string;
}

/** Format `value` with the spec ref's format and unit; `fallbackFormat` applies when the ref has none. */
export function formatSpecRefValue(
    language: RevaLanguage,
    value: unknown,
    ref: RevaSpecOutputRef | null | undefined,
    fallbackFormat?: string | null,
): string {
    return formatTypedValue(language, value, parseGuiType(ref?.format ?? fallbackFormat, ref?.unitDe, ref?.unitEn));
}

/**
 * Format the market/lending value and their per-sqm variants, spec-driven. The lending format
 * falls back to the market format when not given (lending value/spec is often not present).
 */
export function formatMarketAndLendingValue(
    valueAndLendingValue: MarketAndLendingValue | null | undefined,
    valueSpec: MarketAndLendingValueSpec | null | undefined,
    language: RevaLanguage,
): FormattedMarketAndLendingValue {
    return {
        value: formatSpecRefValue(language, valueAndLendingValue?.value, valueSpec?.market),
        valueSqm: formatSpecRefValue(language, valueAndLendingValue?.value_per_sqm, valueSpec?.marketSqm),
        valueLending: formatSpecRefValue(
            language,
            valueAndLendingValue?.lending_value,
            valueSpec?.lending,
            valueSpec?.market?.format,
        ),
        valueLendingSqm: formatSpecRefValue(
            language,
            valueAndLendingValue?.lending_value_per_sqm,
            valueSpec?.lendingSqm,
            valueSpec?.marketSqm?.format,
        ),
    };
}
